import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { L10N_LOCALE, L10nLocale } from 'angular-l10n';
import { BehaviorSubject, Observable, Subscription, debounceTime, map, startWith } from 'rxjs';
import { Survey, SurveyTemplate } from 'src/app/handweave-api';
import { SurveysManagerService } from '../surveys-manager.service';

export interface SurveyResult {
  id?: string | null;
  name?: string | null;
  dateCreated?: Date | null;
}

@Component({
  selector: 'app-surveys-search',
  templateUrl: './surveys-search.component.html',
  styleUrls: ['./surveys-search.component.scss'],
  standalone: false
})
export class SurveysSearchComponent implements OnInit {
  subscription: Subscription = new Subscription();
  lastSearch$: Subscription;
  searchControl = new UntypedFormControl();
  searchQuery: string;
  searchResults: Survey[];
  searchInProgress: boolean = false;

  @Input() placeholder: string;
  @Input() forTemplates: boolean = false;
  @Input() clear: BehaviorSubject<void>;
  @Output() selectionChanged: EventEmitter<Survey|SurveyTemplate|null> = new EventEmitter();

  constructor(
    @Inject(L10N_LOCALE) public locale: L10nLocale,
    private surveysManager: SurveysManagerService,
  ) {}

  ngOnInit(): void {
    this.setupAutocomplete();
  }

  private setupAutocomplete() {
    if (!this.placeholder) this.placeholder = this.forTemplates ? 'Surveys.SearchTemplatePlaceholder' : 'Surveys.SearchSurveyPlaceholder';
    this.subscription.add(this.searchControl.valueChanges.pipe(startWith(''), debounceTime(200)).subscribe(newValue => this.onQueryChanged(newValue)));
    this.clear?.subscribe(() => this.resetSearch());
  }

  private getSearchResults(query: string): Observable<Array<SurveyResult>> {
    if (this.forTemplates) {
      return this.surveysManager.searchTemplates(query).pipe(map(res => res.items));
    } else {
      return this.surveysManager.searchSurveys(query).pipe(map(res => res.items));
    }
  }

  private onQueryChanged(newValue: string) {
    const query = newValue || null;
    this.searchQuery = query;
    this.lastSearch$?.unsubscribe();
    this.searchInProgress = true;
    const sub$ = this.getSearchResults(query).subscribe({
      next: (res) => {
        this.searchInProgress = false;
        this.searchResults = res;
      },
      error: (_err) => {
        this.searchInProgress = false;
        this.searchResults = null;
      }
    });
    this.lastSearch$ = sub$;
    this.subscription.add(sub$);
  }

  resetSearch() {
    this.lastSearch$?.unsubscribe();
    this.searchControl.reset();
    this.onSelectionChanged(null);
  }

  onSelectionChanged(selection: SurveyResult) {
    this.selectionChanged.emit(selection);
  }
}
